import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Col, Row } from "react-bootstrap";
import { Field, Formik, FormikProps } from "formik";
import * as yup from "yup";
import { find, findIndex, get, isEmpty } from "lodash";
import { Button } from "primereact/button";
import { confirmPopup } from "primereact/confirmpopup";
import { InputField } from "../../../../components/Shared/InputField";
import InputAttachment from "../../../../components/Shared/InputAttachment";

interface IProps {
    dropdowns?: any | undefined;
}

const validationSchema = (t: Function): any => {
    return yup.object().shape({
        title: yup.string().required(t('the field is required.', { field: t('title') })).min(3).nullable(),
        document_id: yup.string().required(t('please upload the document')).nullable(),
    });
};

type TInputForm = {
    title: null | string,
    doc_type: null | string,
    document_id: null | string,
};

const DEFAULT_FORM_STATE = {
    title: null,
    doc_type: "general",
    document_id: null,
};

// @ts-ignore
const OpenOrderDamageCaseDocument = forwardRef((props: IProps, ref) => {
    const { dropdowns } = props;
    const formRef: any = useRef();
    const [initFormState, setInitFormState] = useState<any>(DEFAULT_FORM_STATE);
    const [documents, setDocuments] = useState([]);
    const { t }: any = useTranslation();

    // Expose documents to parent component
    useImperativeHandle(ref, () => ({
        getDocuments: () => documents,
        resetDocuments: () => {
            setDocuments([]);
            setInitFormState(DEFAULT_FORM_STATE);
        }
    }));

    const addDocument = async (payloads: TInputForm, { setErrors, resetForm }: any) => {
        let _documents: any = [...documents];
        let existingDocument = find(_documents, { document_id: payloads.document_id });

        if ( !isEmpty(existingDocument) ) {
            const index = findIndex(_documents, { document_id: payloads.document_id });
            existingDocument = payloads;
            _documents[index] = existingDocument;
        } else {
            _documents.push(payloads);
        }

        setDocuments(_documents);
        resetForm();
        setInitFormState(DEFAULT_FORM_STATE);
    };

    const onDelete = ({ id }: any) => {
        if ( find(documents, { document_id: id }) ) {
            // @ts-ignore
            const trashIndex = findIndex(documents, { document_id: id });
            const _documents = documents.filter((_: any, index: number) => ( index != trashIndex ));
            setDocuments(_documents);
        }
    };

    const onEdit = (item: any) => {
        setInitFormState(item);
    };

    const onAfterUpload = (name: any, size: any) => {
        const base: any = get(formRef.current, ['values']);

        setInitFormState({
            ...base,
            title: name,
            size: size
        })
    };

    const onResetForm = () => {
        const base: any = formRef.current;
        if ( base ) {
            base.resetForm();
        }
        setInitFormState(DEFAULT_FORM_STATE);
    };

    return (
        <>
            <div>
                <Formik
                    innerRef={ formRef }
                    enableReinitialize={ true }
                    initialValues={ initFormState }
                    onSubmit={ addDocument }
                    validationSchema={ validationSchema(t) }
                >
                    { (formProps: FormikProps<TInputForm>) => {

                        return (
                            <div>
                                <Row className={ "mt-4 md:mb-3 sm:mb-3" }>
                                    <Col sm={ 12 } md={ 8 }>
                                        <Field component={ InputField }
                                               name="title"
                                               label={ t("title") }
                                               placeholder={ t("enter") + ' ' + t("title").toLowerCase() }
                                               isRequired autoFocus />
                                    </Col>
                                    <Col sm={ 12 } md={ 4 }>
                                        <Field component={ InputAttachment }
                                               name="document_id"
                                               label={ t("upload") }
                                               uploadToAlp={ 1 }
                                               onAfterUpload={ onAfterUpload }
                                        />
                                    </Col>
                                    <Col sm={ 12 } md={ 12 }
                                         className={ "mt-sm-0 mt-md-3 mt-lg-3" }>
                                        <Button type="button" className={ "me-2" } size={ "small" }
                                                disabled={ formProps.isSubmitting } outlined severity={ "success" }
                                                onClick={ () => formProps.submitForm() }
                                                title={ t("add") }>
                                            <i className="pi pi-plus me-2" /> { t("document") }
                                        </Button>
                                        <Button type="button" size={ "small" } outlined
                                                disabled={ formProps.isSubmitting }
                                                onClick={ onResetForm }
                                                title={ t("to make all fields empty") }>
                                            <i className="pi pi-refresh me-2" /> { t("reset form") }
                                        </Button>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col sm={ 12 }>
                                        <table className={"fl-table"}>
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>{ t('title') }</th>
                                                    <th className={ "text-end" }>{ t('action') }</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                { documents && documents.map((item: any, index: number) => {
                                                    return (
                                                        <tr key={ index }>
                                                            <td>{ index + 1 }</td>
                                                            <td>{ item.title }</td>
                                                            <td className={ "text-end" }>
                                                                <a
                                                                    title={ t('edit item') }
                                                                    onClick={ () => onEdit(item) }>
                                                                    <i className="fas fa-edit" />
                                                                </a>
                                                                <a
                                                                    href={ `${ process.env.REACT_APP_DOCUMENT_URL }/${ item.document_id }` }
                                                                    className={ "ms-2 " }
                                                                    target={ "_blank" }
                                                                    rel="noopener noreferrer">
                                                                    <i className="fas fa-cloud-download-alt" />
                                                                </a>
                                                                <a
                                                                    title={ t('delete item') }
                                                                    className={ "ms-2 " }
                                                                    onClick={ (event: any) => confirmPopup({
                                                                        target: event.currentTarget,
                                                                        message: t('do you want to delete this record?'),
                                                                        icon: 'pi pi-info-circle',
                                                                        // @ts-ignorer
                                                                        defaultFocus: 'reject',
                                                                        acceptClassName: 'p-button-danger',
                                                                        accept: () => onDelete({ id: item.document_id }),
                                                                        reject: () => {
                                                                        },
                                                                    }) }>
                                                                    <i className="fas fa-trash-alt" />
                                                                </a>
                                                            </td>
                                                        </tr>
                                                    );
                                                }) }
                                            </tbody>
                                            {
                                                isEmpty(documents) &&
                                              <tfoot>
                                                  <tr>
                                                      <td colSpan={ 6 }>
                                                          { t('no data') }
                                                      </td>
                                                  </tr>
                                              </tfoot>
                                            }
                                        </table>
                                    </Col>
                                </Row>
                            </div>
                        );
                    } }
                </Formik>
            </div>
        </>
    );
});

export default OpenOrderDamageCaseDocument;

